import { useCallback, useMemo, useState } from 'react';
import type {
  ContextSource,
  WorkspaceContext,
  WorkspaceContextAdvisory,
  WorkspaceContextAdvisoryReason,
  WorkspaceContextFile,
  WorkspaceContextFileKind,
  WorkspaceContextFramework,
} from '@localagent/sdk';
import { Badge } from '@localagent/ui/components/badge';
import { Button } from '@localagent/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';
import { ContextSourceList } from '../context/ContextSourceList';
import {
  ContextSourceEditor,
  type ContextSourceEditorValues,
} from '../context/ContextSourceEditor';
import {
  useContextSources,
  useCreateContextSource,
  useDeleteContextSource,
  useMoveContextSource,
  useUpdateContextSource,
  useWorkspaceContext,
} from '../../hooks/useContextSources';

const FRAMEWORK_ORDER: WorkspaceContextFramework[] = ['sot', 'claude', 'cursor', 'codex'];

const FRAMEWORK_LABEL: Record<WorkspaceContextFramework, string> = {
  sot: 'Shogun (AGENTS.md, .shogunchop)',
  claude: 'Claude (CLAUDE.md, .claude)',
  cursor: 'Cursor (.cursorrules, .cursor)',
  codex: 'Codex (AGENTS.override.md)',
};

const KIND_LABEL: Record<WorkspaceContextFileKind, string> = {
  briefing: 'Briefing',
  rule: 'Rule',
  memory: 'Memory',
};

const ADVISORY_LABEL: Record<WorkspaceContextAdvisoryReason, string> = {
  outside_workspace: 'Outside the workspace',
  unrecognized_rule_file: 'Not a rule file',
  malformed_rule: 'Could not be parsed',
  not_always_applied: 'Not always applied',
  duplicate_content: 'Duplicate',
};

export interface WorkspaceContextTabProps {
  workspaceId: string;
  showAdvanced: boolean;
}

export function WorkspaceContextTab({ workspaceId, showAdvanced }: WorkspaceContextTabProps) {
  const contextQuery = useWorkspaceContext(workspaceId);

  return (
    <div className="flex flex-col gap-8" data-testid="workspace-context-tab">
      <section className="flex flex-col gap-4" data-testid="workspace-context-agent-view">
        <div className="flex items-center gap-2">
          <h3 className="text-body-reading font-semibold">What the agent is told</h3>
          {contextQuery.isFetching && contextQuery.data ? (
            <span className="text-caption text-muted-foreground" data-testid="workspace-context-refreshing">
              Refreshing…
            </span>
          ) : null}
        </div>
        {contextQuery.isError ? (
          <div
            className="rounded-md bg-destructive/10 p-3 text-body text-destructive"
            data-testid="workspace-context-error"
          >
            <p className="font-medium">Could not read this workspace&apos;s context.</p>
            <p className="mt-1">{contextQuery.error.message}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              data-testid="workspace-context-retry"
              onClick={() => void contextQuery.refetch()}
            >
              Try again
            </Button>
          </div>
        ) : contextQuery.data ? (
          <AgentContextView context={contextQuery.data} showAdvanced={showAdvanced} />
        ) : (
          <LoadingState
            label="Reading workspace context..."
            data-testid="workspace-context-loading"
          />
        )}
      </section>

      <WorkspaceSourcesSection workspaceId={workspaceId} />
    </div>
  );
}

function AgentContextView({
  context,
  showAdvanced,
}: {
  context: WorkspaceContext;
  showAdvanced: boolean;
}) {
  const filesByFramework = useMemo(() => groupByFramework(context.files), [context.files]);
  const advisoriesByFramework = useMemo(
    () => groupByFramework(context.advisories),
    [context.advisories],
  );

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="workspace-context-view"
      data-fingerprint={context.fingerprint}
    >
      <EnvironmentFacts context={context} showAdvanced={showAdvanced} />

      <div className="flex flex-col gap-3" data-testid="workspace-context-files">
        <h4 className="text-label font-semibold">Instruction files</h4>
        {!context.settings.enabled ? (
          <p className="text-body text-muted-foreground" data-testid="workspace-context-steering-off">
            Workspace instructions are turned off in steering.json; none of these files are sent.
          </p>
        ) : null}
        {context.files.length === 0 && context.advisories.length === 0 ? (
          <p className="text-body text-muted-foreground" data-testid="workspace-context-files-empty">
            No instruction files found. The agent receives no workspace instructions.
          </p>
        ) : null}
        {context.frameworks.map((state) => {
          const files = filesByFramework.get(state.framework) ?? [];
          const advisories = advisoriesByFramework.get(state.framework) ?? [];
          if (files.length === 0 && advisories.length === 0) return null;
          return (
            <div
              key={state.framework}
              className="rounded-md border p-3"
              data-testid={`workspace-context-framework-${state.framework}`}
              data-active={state.active}
            >
              <div className="flex items-center gap-2">
                <span className="text-label font-medium">{FRAMEWORK_LABEL[state.framework]}</span>
                {state.active ? (
                  <Badge variant="secondary">Active</Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    {state.enabled ? 'Not present' : 'Turned off'}
                  </Badge>
                )}
              </div>
              <ul className="mt-2 flex flex-col gap-1">
                {files.map((file) => (
                  <ContextFileRow key={file.path} file={file} />
                ))}
              </ul>
              {advisories.length > 0 ? (
                <div className="mt-3">
                  <p className="text-caption font-medium text-muted-foreground">Skipped</p>
                  <ul className="mt-1 flex flex-col gap-1">
                    {advisories.map((advisory) => (
                      <AdvisoryRow key={`${advisory.path}:${advisory.reason}`} advisory={advisory} />
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-2" data-testid="workspace-context-skills">
        <h4 className="text-label font-semibold">Skills</h4>
        {context.skills.length === 0 ? (
          <p className="text-body text-muted-foreground" data-testid="workspace-context-skills-empty">
            No workspace skills.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {context.skills.map((skill) => (
              <li
                key={skill.qualified_name}
                className="text-body"
                data-testid={`workspace-context-skill-${skill.qualified_name}`}
                data-shadowed={Boolean(skill.shadowed_by)}
              >
                <span className="font-mono">{skill.name}</span>
                <span className="ml-2 text-caption text-muted-foreground">{skill.framework}</span>
                {skill.shadowed_by ? (
                  <span className="ml-2 text-caption text-muted-foreground">
                    hidden by the {skill.shadowed_by} skill of the same name; reach it as{' '}
                    <span className="font-mono">{skill.qualified_name}</span>
                  </span>
                ) : null}
                {skill.summary ? (
                  <p className="text-caption text-muted-foreground">{skill.summary}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EnvironmentFacts({
  context,
  showAdvanced,
}: {
  context: WorkspaceContext;
  showAdvanced: boolean;
}) {
  const env = context.environment;
  const facts = env.facts;
  const rows: Array<[string, string]> = [
    ['Root', env.root],
    ['Kind', env.routing_class === 'cloud' ? 'Cloud' : 'Local'],
    ['OS', facts.os],
    ['Files', `${facts.file_count}${facts.truncated ? '+' : ''}`],
  ];
  if (facts.languages?.length) rows.push(['Languages', facts.languages.join(', ')]);
  if (facts.key_files?.length) rows.push(['Key files', facts.key_files.join(', ')]);
  if (facts.toolchains?.length) rows.push(['Toolchains', facts.toolchains.join(', ')]);
  if (facts.git) {
    rows.push([
      'Git',
      `${facts.git.branch}${facts.git.dirty ? ` (${facts.git.change_count} uncommitted)` : ''}`,
    ]);
  }
  if (showAdvanced) {
    rows.push(['Read from', env.substrate === 'pod' ? 'Cloud pod' : 'Workstation over bridge']);
    if (env.workstation_device_id) rows.push(['Workstation device', env.workstation_device_id]);
    rows.push(['Fingerprint', context.fingerprint]);
  }

  return (
    <div className="flex flex-col gap-2" data-testid="workspace-context-environment">
      <h4 className="text-label font-semibold">Environment</h4>
      {env.environment_unsaved ? (
        <p className="text-body text-muted-foreground" data-testid="workspace-context-environment-unsaved">
          This workspace&apos;s environment has not been saved yet.
        </p>
      ) : null}
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="break-all font-mono text-caption leading-5">{value}</dd>
          </div>
        ))}
      </dl>
      {facts.git && facts.git.recent.length > 0 ? (
        <div data-testid="workspace-context-git-recent">
          <p className="text-caption font-medium text-muted-foreground">Recent commits</p>
          <ul className="mt-1 font-mono text-caption">
            {facts.git.recent.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ContextFileRow({ file }: { file: WorkspaceContextFile }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li
      data-testid={`workspace-context-file-${file.path}`}
      data-kind={file.kind}
      data-injected={file.injected}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left text-body hover:underline"
        aria-expanded={expanded}
        data-testid={`workspace-context-file-toggle-${file.path}`}
        onClick={() => setExpanded((open) => !open)}
      >
        <span aria-hidden>{expanded ? '▾' : '▸'}</span>
        <span className="font-mono">{file.path}</span>
        <Badge variant="outline">{KIND_LABEL[file.kind]}</Badge>
        {!file.injected ? (
          <span
            className="text-caption text-muted-foreground"
            data-testid={`workspace-context-file-excluded-${file.path}`}
          >
            Not sent: turned off in steering.json
          </span>
        ) : null}
      </button>
      {expanded ? (
        <pre
          className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-caption"
          data-testid={`workspace-context-file-content-${file.path}`}
        >
          {file.content}
        </pre>
      ) : null}
    </li>
  );
}

function AdvisoryRow({ advisory }: { advisory: WorkspaceContextAdvisory }) {
  return (
    <li
      className="text-caption text-muted-foreground"
      data-testid={`workspace-context-advisory-${advisory.path}`}
      data-reason={advisory.reason}
    >
      <span className="font-mono">{advisory.path}</span>
      <span className="ml-2">{ADVISORY_LABEL[advisory.reason]}</span>
      {advisory.detail ? <span className="ml-1">({advisory.detail})</span> : null}
    </li>
  );
}

function groupByFramework<T extends { framework: WorkspaceContextFramework }>(
  items: T[],
): Map<WorkspaceContextFramework, T[]> {
  const groups = new Map<WorkspaceContextFramework, T[]>();
  for (const framework of FRAMEWORK_ORDER) groups.set(framework, []);
  for (const item of items) {
    const group = groups.get(item.framework);
    if (!group) throw new Error(`workspace context names unknown framework '${item.framework}'`);
    group.push(item);
  }
  return groups;
}

function WorkspaceSourcesSection({ workspaceId }: { workspaceId: string }) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ContextSource | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleting, setDeleting] = useState<ContextSource | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const sourcesQuery = useContextSources(workspaceId);
  const createMutation = useCreateContextSource(workspaceId);
  const updateMutation = useUpdateContextSource(workspaceId);
  const deleteMutation = useDeleteContextSource(workspaceId);
  const moveMutation = useMoveContextSource(workspaceId);

  const workspaceSources = useMemo(
    () => (sourcesQuery.data?.sources ?? []).filter((s) => s.scope === 'workspace'),
    [sourcesQuery.data],
  );

  const run = useCallback(async (op: Promise<unknown>) => {
    setRowError(null);
    try {
      await op;
    } catch (err) {
      setRowError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const handleSave = async (values: ContextSourceEditorValues) => {
    if (editing) {
      await updateMutation.mutateAsync({
        sourceId: editing.id,
        req: {
          name: values.name,
          ...(values.body !== undefined ? { body: values.body } : {}),
          ...(values.reference_path !== undefined
            ? { reference_path: values.reference_path }
            : {}),
          ...(values.target_persona !== undefined
            ? { target_persona: values.target_persona }
            : {}),
        },
      });
    } else {
      await createMutation.mutateAsync(values);
    }
  };

  const busy =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    moveMutation.isPending;

  return (
    <section className="flex flex-col gap-2" data-testid="workspace-context-sources">
      <h3 className="text-body-reading font-semibold">Workspace sources</h3>
      <p className="text-body text-muted-foreground">
        Context sources that apply only to agents working in this workspace.
      </p>
      <ContextSourceList
        sources={workspaceSources}
        isLoading={sourcesQuery.isLoading}
        isError={sourcesQuery.isError}
        error={sourcesQuery.error}
        searchValue={search}
        onSearchChange={setSearch}
        onCreate={() => {
          setEditing(null);
          setEditorOpen(true);
        }}
        onEdit={(source) => {
          setEditing(source);
          setEditorOpen(true);
        }}
        onDelete={setDeleting}
        onToggle={(source, enabled) =>
          void run(updateMutation.mutateAsync({ sourceId: source.id, req: { enabled } }))
        }
        onMove={(source, position) =>
          void run(moveMutation.mutateAsync({ sourceId: source.id, position }))
        }
        rowError={rowError}
        busy={busy}
      />

      <ContextSourceEditor
        source={editing}
        open={editorOpen}
        workspaceId={workspaceId}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />

      <Dialog
        open={deleting !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setDeleting(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this source?</DialogTitle>
            <DialogDescription>
              {deleting
                ? `"${deleting.name}" will stop being given to agents in this workspace. This cannot be undone.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="context-source-delete-confirm"
              disabled={deleteMutation.isPending}
              onClick={() => {
                const target = deleting;
                if (!target) return;
                setDeleting(null);
                void run(deleteMutation.mutateAsync(target.id));
              }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
